import AppHeadTags from '@chegg-wt/core/components/Head/AppHeadTags'
import AppLayout from '@chegg-wt/core/components/AppLayout'
import RoutesContext from '@chegg-wt/core/components/RoutesContext'
import useAppendStylesheet from '@chegg-wt/core/hooks/useAppendStylesheet'
import useAppScroll from '@chegg-wt/core/hooks/useAppScroll'
import useCloseModal from '@chegg-wt/core/hooks/useCloseModal'
import useErrorLogger from '@chegg-wt/core/hooks/useErrorLogger'
import useInitUser from '@chegg-wt/core/hooks/useInitUser'
import useInitGTM from '@chegg-wt/core/hooks/useInitGTM'
import useOnPageLoad from '@chegg-wt/core/hooks/useOnPageLoad'
import useUrlStateChange from '@chegg-wt/core/hooks/useUrlStateChange'
import { AppState } from '@chegg-wt/core/state'
import { Config } from '@chegg-wt/core/config'
import { ThemeProvider } from 'styled-components'
import { SiteTheme } from '@chegg-wt/core/theme/styled'
import {
  lvpR3LayoutSelector,
  svpVariantSelector,
  adConfigVersionSelector,
  stickyDisabledSelector,
} from '@chegg-wt/core/state/featureFlags/selectors'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { CheggLayoutProvider } from '@chegg-wt/core/components/CheggLayout/CheggLayoutProvider'
import { useSetVersion } from '@chegg-wt/core/hooks/useSetVersion'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import useCurrentPage from '@chegg-wt/core/hooks/useCurrentPage'
import { Environment, init, listen } from '@chegg/one-auth-sdk'
import { isProduction } from '@chegg-wt/core/utils/env'
import { Cookies, getCookie } from '@chegg-wt/core/utils/cookies'
import { getUserAndToken } from '@chegg-wt/core/api/user'
import { setEbUser } from '@chegg-wt/core/utils/localStorage'
import { withOptimizelyComponent } from '../hocs/withOptimizely'
import { authRoutes } from '../constants/user'
import { hasSideViewForRoute } from '../utils/adframe'
import {
  AppRouteCreators,
  GetFromQuery,
  IsExact,
  HeadTags,
  NavItems,
  LegalContents,
  FooterColumn,
} from '../types/core'
import { isProSelector } from '../state/user/selectors'
import {
  AUTOCITE_V0_RIO_EVENTS,
  SEARCH_API_EXPERIMENT,
  UPDATE_AUTOCITE_API,
  UPDATE_MESSAGING_FEATURE_CM,
  SearchApiVariant,
} from '../state/optimizely/constants'
import {
  setAutociteApiVersion,
  setAutociteV0RioOptFlag,
  setSearchApiVariant,
} from '../state/createCitation/actions'
import { setUpdateMessagingFlag } from '../state/app/actions'
import { useOptimizelyExperiment } from '../hooks/useOptimizelyExperiment'
import { ExportTabId } from './Modals/ExportModal/types'
import { LvpProvider } from './Layouts/LargeVideoPlayerLayout'
import Error404 from './Layouts/Error404'
import { getProfileCode } from './Auth/OneAuth/utils'

if (getCookie(Cookies.OneAuth) === 'true') {
  const env = isProduction() ? Environment.prod : Environment.test

  init({
    env,
    clientId: getProfileCode(Config.tokenSiteCode),
    domainUrl: Config.cookieDomain,
  })

  const refreshSuccessCallback = async (token) => {
    if (token) {
      const user = await getUserAndToken()
      setEbUser(user)
    }
  }
  listen('refresh.success', refreshSuccessCallback)
}

interface Props {
  head: HeadTags
  userAgent: string
  adConfigVersion?: string
  isCheggFree?: boolean
  routes: AppRouteCreators
  theme: SiteTheme
  navItemsSelector: (state: AppState) => NavItems
  notificationBanner?: React.ComponentType
  exportTabIds?: ExportTabId[]
  footerContents: {
    footerLinkColumns: FooterColumn[]
    legalContents: LegalContents
    ccpa: boolean
  }
  hideLayout?: boolean
  getSourceFromQuery?: GetFromQuery
  getStyleFromQuery?: GetFromQuery
  isSourceExact?: IsExact
  isStyleExact?: IsExact
  usesCheggLayout?: boolean
  useAdframeLayout?: boolean
  lvpR3?: boolean
  isPro?: boolean
  stickyDisabled?: boolean
  isAdsFree?: boolean
  getActualStyleId?: (style: string) => string
  enableSvp?: boolean
  svpVariant?: string
  showBanner?: boolean
  bannerText?: string
  isHomePage?: boolean
  fullWidthBannerProps: {
    backgroundColour: string
    minHeight?: string
  }
  setAutociteV0RioOptFlag: (autociteV0RioOptFlag: boolean) => void
  setAutociteApiVersion: (autociteApiVersion: string) => void
  setUpdateMessagingFlag: (updateMessaginFlag: boolean) => void
  setSearchApiVariant: (searchApiVarient: SearchApiVariant) => void
}

export const App: React.FC<Props> = ({
  children,
  userAgent,
  isCheggFree,
  head,
  navItemsSelector,
  routes,
  theme,
  exportTabIds,
  notificationBanner,
  footerContents,
  hideLayout,
  getSourceFromQuery,
  isSourceExact,
  getStyleFromQuery,
  isStyleExact,
  usesCheggLayout = false,
  useAdframeLayout = false,
  isPro,
  lvpR3,
  stickyDisabled,
  isAdsFree,
  getActualStyleId,
  adConfigVersion,
  enableSvp,
  svpVariant,
  showBanner,
  bannerText,
  isHomePage,
  fullWidthBannerProps,
  setAutociteV0RioOptFlag,
  setAutociteApiVersion,
  setUpdateMessagingFlag,
  setSearchApiVariant,
}) => {
  // useErrorLogger should be first to ensure we are able to log all errors
  useErrorLogger()
  const router = useRouter()
  useInitGTM()
  useAppendStylesheet()
  useAppScroll()
  useCloseModal()
  useInitUser()
  useSetVersion()
  const { routeName } = useOnPageLoad({
    routes,
    getSourceFromQuery,
    getStyleFromQuery,
    getActualStyleId,
  })
  const { urlIsValid } = useUrlStateChange({
    getSourceFromQuery,
    isSourceExact,
    getStyleFromQuery,
    isStyleExact,
    getActualStyleId,
  })

  const { isEnabled: autociteV0RioEnabled, clientReady } = useSafeOptimizelyFeature(
    AUTOCITE_V0_RIO_EVENTS
  )
  useEffect(() => {
    if (clientReady) {
      setAutociteV0RioOptFlag(autociteV0RioEnabled)
    }
  }, [autociteV0RioEnabled, clientReady, setAutociteV0RioOptFlag])

  const {
    variation: updateAutociteApiVariation,
    clientReady: updateAutociteApiClientReady,
  } = useOptimizelyExperiment(UPDATE_AUTOCITE_API)

  const {
    variation: searchApiExperimentVariation,
    clientReady: searchApiExperimentClientReady,
  } = useOptimizelyExperiment(SEARCH_API_EXPERIMENT)

  useEffect(() => {
    if (searchApiExperimentClientReady) {
      setSearchApiVariant(searchApiExperimentVariation as SearchApiVariant)
    }
  }, [searchApiExperimentClientReady, searchApiExperimentVariation])

  useEffect(() => {
    if (updateAutociteApiClientReady) {
      setAutociteApiVersion(updateAutociteApiVariation)
    }
  }, [updateAutociteApiVariation, updateAutociteApiClientReady])

  const { appName } = Config

  const {
    isEnabled: updateMessagingCMOptFlag,
    clientReady: updateMsgCMFeatureclientReady,
  } = useSafeOptimizelyFeature(UPDATE_MESSAGING_FEATURE_CM)

  useEffect(() => {
    if (updateMsgCMFeatureclientReady && appName === 'cm') {
      setUpdateMessagingFlag(updateMessagingCMOptFlag)
    }
  }, [updateMsgCMFeatureclientReady])

  const NotificationBanner = notificationBanner
  const hideAdRail = authRoutes.includes(router.pathname)
  const errorPageMaybe = urlIsValid ? null : <Error404 />

  // determine whether we should should the lvp for a particular page and given the user role
  const showLvp =
    !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro && !useAdframeLayout
  const showSvp = enableSvp && useAdframeLayout && !isAdsFree && !isPro
  const showLvpR3 = !!lvpR3 && !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro
  const isStickyDisabled =
    !!stickyDisabled && !!hasSideViewForRoute(routeName) && !isAdsFree && !isPro

  const props = {
    isCheggFree,
    exportTabIds,
    children,
    hideLayout,
    hideAdRail,
    useAdframeLayout,
    errorPageMaybe,
    navItemsSelector,
    NotificationBanner,
    footerContents,
    routeName,
    isAdsFree,
    isPro,
    showLvp,
    showSvp,
    svpVariant,
    showBanner,
    adConfigVersion,
    bannerText,
    isHomePage,
    fullWidthBannerProps,
  }

  return (
    <>
      <AppHeadTags {...head} />
      <CheggLayoutProvider userAgent={userAgent} skip={!usesCheggLayout}>
        {/* Using any as @chegg-ui/theme requires it to pass cuiTheme property in theme but we are using different custom theme in UCF */}
        <ThemeProvider theme={theme as any}>
          <RoutesContext.Provider value={routes}>
            <LvpProvider
              adConfigVersion={adConfigVersion}
              showLvp={showLvp}
              isLvpR3Active={showLvpR3}
              isStickyDisabled={isStickyDisabled}
              currentPage={useCurrentPage()}
            >
              <AppLayout {...props} />
            </LvpProvider>
          </RoutesContext.Provider>
        </ThemeProvider>
      </CheggLayoutProvider>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  isPro: isProSelector(state),
  lvpR3: lvpR3LayoutSelector(state),
  stickyDisabled: stickyDisabledSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  svpVariant: svpVariantSelector(state),
})

const mapDispatchToProps = {
  setAutociteV0RioOptFlag,
  setUpdateMessagingFlag,
  setAutociteApiVersion,
  setSearchApiVariant,
}

export default connect(mapStateToProps, mapDispatchToProps)(withOptimizelyComponent(App))
